import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import { WidgetChart } from '@t-one-internal/design-system';
import type { ChartFieldMapping } from '../../types/reportBuilder';

const NEEDS_VALUE2 = new Set(['scatter']);
const SUPPORTS_SERIES = new Set(['bar', 'column', 'line', 'area', 'stacked', 'h-bar']);

function ChartConfigurator() {
  const { results, chartType, chartFieldMapping, showLegend, showDataLabels, setChartFieldMapping, setShowLegend, setShowDataLabels } = useReportBuilderStore();

  if (!results || !chartFieldMapping) return null;

  const dims = results.columns.filter(c => c.is_dimension || c.is_time_grouping);
  const measures = results.columns.filter(c => c.is_measure);

  const update = (patch: Partial<ChartFieldMapping>) => {
    setChartFieldMapping({ ...chartFieldMapping, ...patch });
  };

  const selectClass = 'rounded border border-gray-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none';

  return (
    <div className="flex flex-wrap items-center gap-3 rounded border bg-gray-50 px-3 py-2">
      <div className="flex items-center gap-1">
        <span className="text-[10px] font-medium text-gray-500">X-axis:</span>
        <select
          value={chartFieldMapping.nameField}
          onChange={(e) => update({ nameField: e.target.value })}
          className={selectClass}
        >
          {dims.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>

      <div className="flex items-center gap-1">
        <span className="text-[10px] font-medium text-gray-500">Y-axis:</span>
        <select
          value={chartFieldMapping.valueField}
          onChange={(e) => update({ valueField: e.target.value })}
          className={selectClass}
        >
          {measures.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>

      {NEEDS_VALUE2.has(chartType) && (
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-medium text-gray-500">Y2-axis:</span>
          <select
            value={chartFieldMapping.value2Field ?? ''}
            onChange={(e) => update({ value2Field: e.target.value || undefined })}
            className={selectClass}
          >
            <option value="">None</option>
            {measures.filter(c => c.key !== chartFieldMapping.valueField).map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
      )}

      {SUPPORTS_SERIES.has(chartType) && dims.length >= 2 && (
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-medium text-gray-500">Series:</span>
          <select
            value={chartFieldMapping.seriesField ?? ''}
            onChange={(e) => update({ seriesField: e.target.value || undefined })}
            className={selectClass}
          >
            <option value="">None</option>
            {dims.filter(c => c.key !== chartFieldMapping.nameField).map(c => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="ml-auto flex items-center gap-3">
        <label className="flex items-center gap-1 text-[10px] text-gray-500">
          <input type="checkbox" checked={showLegend} onChange={(e) => setShowLegend(e.target.checked)} className="h-3 w-3" />
          Legend
        </label>
        <label className="flex items-center gap-1 text-[10px] text-gray-500">
          <input type="checkbox" checked={showDataLabels} onChange={(e) => setShowDataLabels(e.target.checked)} className="h-3 w-3" />
          Labels
        </label>
      </div>
    </div>
  );
}

export default function ChartView() {
  const { results, chartType, chartFieldMapping, showLegend, showDataLabels, setViewMode } = useReportBuilderStore();
  if (!results || !chartFieldMapping) return null;

  // Resolve human-readable labels from column metadata
  const nameCol = results.columns.find(c => c.key === chartFieldMapping.nameField);
  const valueCol = results.columns.find(c => c.key === chartFieldMapping.valueField);
  const value2Col = chartFieldMapping.value2Field ? results.columns.find(c => c.key === chartFieldMapping.value2Field) : undefined;

  return (
    <div className="space-y-3">
      <ChartConfigurator />
      <div className="h-[500px] w-full rounded-lg border border-gray-200 bg-white p-4">
        <WidgetChart
          type={chartType}
          data={results.data}
          config={{
            keys: {
              name: chartFieldMapping.nameField,
              value: chartFieldMapping.valueField,
              ...(chartFieldMapping.value2Field ? { value2: chartFieldMapping.value2Field } : {}),
              ...(chartFieldMapping.seriesField ? { series: chartFieldMapping.seriesField } : {}),
            },
            labels: {
              name: nameCol?.label,
              value: valueCol?.label,
              ...(value2Col ? { value2: value2Col.label } : {}),
            },
            options: { show_legend: showLegend, show_data_labels: showDataLabels },
          }}
          onPointClick={() => setViewMode('table')}
        />
      </div>
    </div>
  );
}
